// Google Analytics 4 with Consent Mode. GA only loads after the visitor
// accepts analytics cookies in the cookie banner (CookieConsent.tsx).

// GA4 measurement ID, e.g. 'G-ABC123XYZ9'. Analytics stays off while this is empty.
export const GA_MEASUREMENT_ID = '';

interface ConsentChoices {
  analytics: boolean;
  marketing: boolean;
}

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let gtag: Gtag | null = null;
let analyticsAllowed = false;

const loadGoogleAnalytics = (): Gtag => {
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  const tag: Gtag = function () {
    // gtag.js reads the arguments object itself; a rest array won't work
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag = tag;

  tag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  tag('js', new Date());

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  return tag;
};

// Called by the cookie banner whenever the visitor's choice is known or changes.
export const applyConsent = ({ analytics, marketing }: ConsentChoices) => {
  analyticsAllowed = analytics;
  if (!GA_MEASUREMENT_ID || (!analytics && !gtag)) return;

  const firstLoad = !gtag;
  if (!gtag) gtag = loadGoogleAnalytics();

  gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
  if (firstLoad) gtag('config', GA_MEASUREMENT_ID);
};

export const trackEvent = (name: string, params: Record<string, string> = {}) => {
  if (gtag && analyticsAllowed) gtag('event', name, params);
};

// A quote request that Netlify Forms accepted.
export const trackLead = (formName: string, serviceType: string) => {
  trackEvent('generate_lead', { form_name: formName, service_type: serviceType });
};

// Counts taps on every phone (tel:) and text (sms:) link on the site.
export const trackContactClicks = () => {
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest<HTMLAnchorElement>('a[href^="tel:"], a[href^="sms:"]');
    if (!link) return;

    trackEvent(link.href.startsWith('sms:') ? 'click_to_text' : 'click_to_call', {
      link_url: link.href,
      page_path: window.location.pathname,
    });
  });
};
